import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Edit3,
    Calendar,
    Clock,
    AlertCircle,
    MessageSquare,
    Users,
    Paperclip,
    Send,
    Download,
    History,
    Briefcase,
    Info,
    Check,
    TrendingUp,
    ExternalLink,
    Loader2,
    Tag,
    Trash2,
    X
} from 'lucide-react';
import api, { getStorageUrl } from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import TaskDrawer from '../../components/Tasks/TaskDrawer';
import styles from './ViewTask.module.css';
import NotFound from '../Error/NotFound';


const ViewTask = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [task, setTask] = useState(null);
    const [comment, setComment] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [submittingComment, setSubmittingComment] = useState(false);
    const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    const fetchTask = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get(`/tasks/${id}`);
            if (response.data.success) {
                setTask(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching task details:', error);
            toast.error('Failed to load task details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchTask();
    }, [fetchTask]);

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!comment.trim() && attachments.length === 0) return;

        setSubmittingComment(true);

        try {
            if (attachments.length === 0) {
                const formData = new FormData();
                formData.append('task_id', id);
                formData.append('comment', comment);
                await api.post(`/tasks/${id}/comments`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                for (let i = 0; i < attachments.length; i++) {
                    const formData = new FormData();
                    formData.append('task_id', id);
                    
                    // The first file gets the user's comment, others get a generic text if the user comment is empty
                    // Since comment is required, we provide a default text
                    const currentComment = (i === 0 && comment.trim()) ? comment : `Attached file: ${attachments[i].name}`;
                    formData.append('comment', currentComment);
                    formData.append('attachment', attachments[i]);

                    await api.post(`/tasks/${id}/comments`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                }
            }

            toast.success('Update posted successfully');
            setComment('');
            setAttachments([]);
            fetchTask();
        } catch (error) {
            console.error('Error adding comment:', error);
            toast.error('Failed to post update');
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleRemoveAttachment = (indexToRemove) => {
        setAttachments(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleDeleteComment = async (commentId) => {
        Swal.fire({
            html: `
                <div class="custom-icon-wrapper-swal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                </div>
                <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">Delete Comment?</h2>
                <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
                    Are you sure you want to remove this update?
                </p>
                <p style="margin: 0 0 0 0; color: #94a3b8; font-size: 13px;">
                    This action will permanently remove this comment from the timeline.
                </p>
            `,
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'No, Keep it',
            customClass: {
                popup: 'custom-swal-popup',
                confirmButton: 'custom-swal-confirm',
                cancelButton: 'custom-swal-cancel',
                actions: 'custom-swal-actions-row'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await api.delete(`/tasks/comments/${commentId}`);
                    if (response.data.success) {
                        toast.success('Comment removed');
                        fetchTask();
                    }
                } catch (error) {
                    console.error('Error deleting comment:', error);
                    toast.error('Failed to delete comment');
                }
            }
        });
    };

    const handleToggleComplete = async (e) => {
        const isChecking = e.target.checked;
        const newStatus = isChecking ? 'completed' : 'in_progress';

        Swal.fire({
            html: `
                <div class="custom-icon-wrapper-swal" style="background-color: ${isChecking ? '#dcfce7' : '#fff7ed'}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="${isChecking ? '#16a34a' : '#f97316'}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        ${isChecking 
                            ? '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>' 
                            : '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>'}
                    </svg>
                </div>
                <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">${isChecking ? 'Mark as Complete?' : 'Re-open Task?'}</h2>
                <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
                    Are you sure you want to ${isChecking ? 'mark this task as finished' : 'move this task back to work'}?
                </p>
            `,
            showCancelButton: true,
            confirmButtonText: isChecking ? 'Yes, Complete it' : 'Yes, Re-open',
            cancelButtonText: 'Cancel',
            customClass: {
                popup: 'custom-swal-popup',
                confirmButton: 'custom-swal-confirm-success',
                cancelButton: 'custom-swal-cancel',
                actions: 'custom-swal-actions-row'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await api.put(`/tasks/${id}`, { status: newStatus });
                    if (response.data.success) {
                        toast.success(`Task ${isChecking ? 'completed' : 're-opened'}`);
                        fetchTask();
                    }
                } catch (error) {
                    console.error('Error updating task status:', error);
                    toast.error('Failed to update task status');
                }
            }
        });
    };

    if (loading) {
        return (
            <div className={styles['view-task-container']}>
                <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
                <div className={styles['content-wrapper']}>
                    <Header onMenuClick={() => setIsSidebarOpen(true)} />
                    <div className={styles['loading-container']}>
                        <Loader2 className={styles['spinner']} size={40} />
                        <p>Loading Task Details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!task) {
        return <NotFound />;
    }


    // Calculate time status
    const dueDate = task?.due_date ? new Date(task.due_date) : null;
    const today = new Date();
    const diffTime = dueDate ? dueDate - today : null;
    const diffDays = diffTime ? Math.ceil(diffTime / (1000 * 60 * 60 * 24)) : null;

    // Aggregate attachments
    const allAttachments = task?.comments?.filter(c => c.attached_filepath).map(c => ({
        id: c.id,
        filename: c.attached_filename,
        filepath: c.attached_filepath,
        date: c.created_at,
        user: c.user?.name
    })) || [];

    // Group comments to merge multiple attachments from the same submission
    const groupedComments = [];
    if (task?.comments?.length > 0) {
        task.comments.forEach(currentComment => {
            if (groupedComments.length === 0) {
                groupedComments.push({
                    ...currentComment,
                    attachments: currentComment.attached_filepath ? [{
                        id: currentComment.id,
                        filename: currentComment.attached_filename,
                        filepath: currentComment.attached_filepath
                    }] : []
                });
                return;
            }

            const lastGroup = groupedComments[groupedComments.length - 1];
            const timeDiff = Math.abs(new Date(currentComment.created_at) - new Date(lastGroup.created_at));
            
            // If same user, within 5 seconds, we consider it the same submission batch
            if (lastGroup.user_id === currentComment.user_id && timeDiff < 5000) {
                if (currentComment.attached_filepath) {
                    lastGroup.attachments.push({
                        id: currentComment.id,
                        filename: currentComment.attached_filename,
                        filepath: currentComment.attached_filepath
                    });
                }
                // If current comment has real text and last group has generic text, update text
                if (!currentComment.comment.startsWith('Attached file: ') && lastGroup.comment.startsWith('Attached file: ')) {
                    lastGroup.comment = currentComment.comment;
                }
            } else {
                groupedComments.push({
                    ...currentComment,
                    attachments: currentComment.attached_filepath ? [{
                        id: currentComment.id,
                        filename: currentComment.attached_filename,
                        filepath: currentComment.attached_filepath
                    }] : []
                });
            }
        });
    }

    return (
        <div className={styles['view-task-container']}>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

            <div className={styles['content-wrapper']}>
                <Header onMenuClick={() => setIsSidebarOpen(true)} />

                <main className={styles['main-content']}>
                    <Breadcrumbs customItems={[
                        { label: 'Tasks', path: '/tasks' },
                        { label: `View Task #${id}` }
                    ]} />

                    {/* Page Header */}
                    <div className={styles['page-header']}>
                        <div className={styles['header-title']}>
                            <h1>Task Dashboard</h1>
                            <p>Overview of objectives, assignments, and team discussion.</p>
                        </div>
                        <div className={styles['btn-actions']}>
                            <button className={styles['btn-secondary']} onClick={() => navigate('/tasks')}>
                                <ArrowLeft size={18} />
                                <span>Back</span>
                            </button>
                            {(currentUser?.id === task.created_by || currentUser?.is_admin) && (
                                <button className={styles['btn-primary']} onClick={() => setIsEditDrawerOpen(true)}>
                                    <Edit3 size={18} />
                                    <span>Edit Task</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Dashboard Header Card */}
                    <div className={styles['task-dashboard-header']}>
                        <div className={styles['header-top']}>
                            <div className={styles['title-flex']}>
                                <h1>{task.title}</h1>
                                <span className={`${styles['status-badge']} ${styles['status-' + task.status]}`}>
                                    <div className={styles['dot']}></div>
                                    {task.status?.replace('_', ' ').toUpperCase()}
                                </span>
                            </div>
                        </div>

                        <div className={styles['task-meta']}>
                            <div className={styles['meta-item']}>
                                <Briefcase size={16} />
                                <span>Project: <strong>{task.project?.project_name || 'General Task'}</strong></span>
                            </div>
                            <div className={styles['meta-item']}>
                                <Tag size={16} />
                                <span>Priority: <strong>{task.priority?.toUpperCase()}</strong></span>
                            </div>
                            <div className={styles['meta-item']}>
                                <Calendar size={16} />
                                <span>Deadline: <strong>{task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'No Deadline'}</strong></span>
                            </div>
                        </div>
                    </div>

                    {/* Metrics Row */}
                    <div className={styles['metrics-row']}>
                        <div className={styles['metric-card']}>
                            <div className={styles['m-header']}>
                                <div className={`${styles['m-icon']} ${styles['bg-team']}`}><Users size={20} /></div>
                            </div>
                            <div className={styles['m-info']}>
                                <label>Assigned Team</label>
                                <h2>{task.assigned_employees?.length || 0} Members</h2>
                            </div>
                        </div>
                        <div className={styles['metric-card']}>
                            <div className={styles['m-header']}>
                                <div className={`${styles['m-icon']} ${styles['bg-time']}`}><Clock size={20} /></div>
                                {diffDays !== null && (
                                    <span className={styles['m-trend']} style={{ color: diffDays < 0 ? '#ef4444' : '#10b981' }}>
                                        {diffDays < 0 ? 'Overdue' : `${diffDays}d left`}
                                    </span>
                                )}
                            </div>
                            <div className={styles['m-info']}>
                                <label>Time Management</label>
                                <h2>{task.status === 'completed' ? 'Finished' : (diffDays < 0 ? 'Delayed' : 'Active')}</h2>
                            </div>
                        </div>
                        <div className={styles['metric-card']}>
                            <div className={styles['m-header']}>
                                <div className={`${styles['m-icon']} ${styles['bg-chat']}`}><MessageSquare size={20} /></div>
                            </div>
                            <div className={styles['m-info']}>
                                <label>Discussion Hub</label>
                                <h2>{task.comments?.length || 0} Updates</h2>
                            </div>
                        </div>
                        <div className={styles['metric-card']}>
                            <div className={styles['m-header']}>
                                <div className={`${styles['m-icon']} ${styles['bg-priority']}`}><TrendingUp size={20} /></div>
                            </div>
                            <div className={styles['m-info']}>
                                <label>Task Standing</label>
                                <h2 style={{ color: task.priority === 'high' ? '#ef4444' : '#1e293b' }}>{task.priority?.toUpperCase()}</h2>
                            </div>
                        </div>
                    </div>

                    {/* Content Grid */}
                    <div className={styles['dashboard-grid']}>
                        <div className={styles['left-column']}>
                            {/* Objective Card */}
                            <div className={`${styles['standard-card']} ${styles['objective-card']}`}>
                                <div className={styles['card-header']}>
                                    <div className={styles['card-icon']}><Info size={18} /></div>
                                    <h3>Task Objective</h3>
                                </div>
                                <div className={styles['description-box']}>
                                    {task.description || 'No detailed description provided for this task.'}
                                </div>
                            </div>

                            {/* Discussion Card */}
                            <div className={styles['standard-card']}>
                                <div className={styles['card-header']}>
                                    <div className={styles['card-icon']}><History size={18} /></div>
                                    <h3>Discussion & Timeline</h3>
                                </div>

                                <div className={styles['discussion-actions-row']}>
                                    <div className={styles['complete-toggle-wrapper']}>
                                        <input
                                            type="checkbox"
                                            id="complete-task-check"
                                            checked={task.status === 'completed'}
                                            onChange={handleToggleComplete}
                                        />
                                        <label htmlFor="complete-task-check">Mark Task as Complete</label>
                                    </div>
                                </div>

                                <div className={styles['comment-input-box']}>
                                    <textarea
                                        className={styles['comment-textarea']}
                                        placeholder="Post a progress update or task-related query..."
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                    ></textarea>
                                    <div className={styles['attachment-row']}>
                                        <label className={styles['file-input-wrapper']}>
                                            <Paperclip size={18} />
                                            <input
                                                type="file"
                                                multiple
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    const newFiles = Array.from(e.target.files);
                                                    if (newFiles.length > 0) {
                                                        setAttachments(prev => [...prev, ...newFiles]);
                                                    }
                                                    e.target.value = null;
                                                }}
                                            />
                                            <span>Attach files</span>
                                        </label>
                                        <button
                                            className={styles['btn-primary-sm']}
                                            onClick={handleCommentSubmit}
                                            disabled={submittingComment}
                                        >
                                            {submittingComment ? <Loader2 className={styles['spinner']} /> : <Send size={16} />}
                                            <span>Post Update</span>
                                        </button>
                                    </div>
                                    {attachments.length > 0 && (
                                        <div className={styles['selected-attachments-list']} style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {attachments.map((file, idx) => (
                                                <div key={idx} className={styles['selected-attachment-item']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                    <span className={styles['attachment-name']} style={{ fontSize: '13px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                                    <button 
                                                        type="button" 
                                                        className={styles['remove-attachment-btn']}
                                                        onClick={() => handleRemoveAttachment(idx)}
                                                        title="Remove file"
                                                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', borderRadius: '4px' }}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className={styles['comment-list']}>
                                    {groupedComments?.length > 0 ? (
                                        groupedComments.map((c) => (
                                            <div key={c.id} className={styles['comment-item']}>
                                                <div className={styles['avatar']}>
                                                    {c.user?.name?.charAt(0) || 'U'}
                                                </div>
                                                <div className={styles['comment-bubble']}>
                                                    <div className={styles['comment-header']}>
                                                        <span className={styles['author']}>{c.user?.name || 'Unknown User'}</span>
                                                        <div className={styles['comment-meta-right']}>
                                                            <span className={styles['time']}>
                                                                {new Date(c.created_at).toLocaleString()}
                                                            </span>
                                                            {(currentUser?.id === c.user_id || currentUser?.is_admin) && (
                                                                <button
                                                                    className={styles['delete-comment-btn']}
                                                                    onClick={(e) => { e.stopPropagation(); handleDeleteComment(c.id); }}
                                                                    title="Delete update"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={styles['comment-text']}>{c.comment}</div>
                                                    {c.attachments && c.attachments.length > 0 && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                                                            {c.attachments.map((att) => (
                                                                <a
                                                                    key={att.id}
                                                                    href={getStorageUrl(att.filepath)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className={styles['doc-box']}
                                                                    style={{ background: 'white' }}
                                                                >
                                                                    <div className={styles['doc-icon']}><Paperclip size={16} /></div>
                                                                    <div className={styles['doc-info']}>
                                                                        <span>{att.filename}</span>
                                                                        <small>Click to view</small>
                                                                    </div>
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '40px', background: '#f8fafc', borderRadius: '16px', color: '#94a3b8' }}>
                                            <MessageSquare size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                                            <p>No discussion points yet. Be the first to start the conversation.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={styles['right-column']}>
                            {/* Team Card */}
                            <div className={styles['standard-card']}>
                                <div className={styles['card-header']}>
                                    <div className={styles['card-icon']}><Users size={20} /></div>
                                    <h3>Workforce</h3>
                                </div>
                                <div className={styles['stakeholder-list']}>
                                    {task.assigned_employees?.length > 0 ? (
                                        task.assigned_employees.map(e => (
                                            <div key={e.id} className={styles['sh-item']}>
                                                <div className={styles['sh-avatar']}>{e.employee_name?.charAt(0)}</div>
                                                <div className={styles['sh-info']}>
                                                    <h4>{e.employee_name}</h4>
                                                    <p>Assigned Resource</p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p style={{ color: '#94a3b8', fontSize: '13px' }}>No resources assigned to this task yet.</p>
                                    )}
                                </div>
                            </div>

                            {/* Aggregated Documentation */}
                            <div className={styles['standard-card']}>
                                <div className={styles['card-header']}>
                                    <div className={styles['card-icon']}><Download size={18} /></div>
                                    <h3>Library</h3>
                                </div>
                                {allAttachments.length > 0 ? (
                                    allAttachments.map((doc) => (
                                        <a key={doc.id} href={getStorageUrl(doc.filepath)} target="_blank" rel="noopener noreferrer" className={styles['doc-box']}>
                                            <div className={styles['doc-icon']}><Paperclip size={16} /></div>
                                            <div className={styles['doc-info']}>
                                                <span>{doc.filename}</span>
                                                <small>{doc.user} · {new Date(doc.date).toLocaleDateString()}</small>
                                            </div>
                                        </a>
                                    ))
                                ) : (
                                    <p style={{ color: '#94a3b8', fontSize: '13px' }}>No documents attached to this task.</p>
                                )}
                            </div>

                            {/* Lifecycle card */}
                            <div className={styles['standard-card']}>
                                <div className={styles['card-header']}>
                                    <div className={styles['card-icon']}><Clock size={20} /></div>
                                    <h3>Timeline</h3>
                                </div>
                                <div className={styles['timeline-list']}>
                                    <div className={styles['timeline-item']}>
                                        <div className={styles['tl-icon']} style={{ background: '#f0fdf4', color: '#16a34a' }}>
                                            <Check size={16} />
                                        </div>
                                        <div className={styles['tl-content']}>
                                            <h4>Task Established</h4>
                                            <p>{new Date(task.created_at).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                    <div className={styles['timeline-item']}>
                                        <div className={styles['tl-icon']}>
                                            <ExternalLink size={16} />
                                        </div>
                                        <div className={styles['tl-content']}>
                                            <h4>Current Status</h4>
                                            <p>{task.status?.toUpperCase() || 'STEADY'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div> {/* close right-column */}
                    </div> {/* close dashboard-grid */}
                    <Footer />

                </main>
            </div>
            <TaskDrawer
                isOpen={isEditDrawerOpen}
                onClose={() => setIsEditDrawerOpen(false)}
                taskData={task ? { id: task.id } : null}
                onSave={fetchTask}
            />
        </div>
    );
};

export default ViewTask;
